// Basic
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
// Libs
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '../../../../../common-utils/libraries/tailwind/utils';
// Store & actions
import actionsMerchantAccounts from '../../api/actionsMerchantAccounts';
// Components
import HeaderTitle from '../../../../../common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import FooterForm from '../../../../../common-utils/components/renders/modal/components/footers/FooterForm/FooterForm';
import Entity from '../../../../../common-utils/components/renders/entity/Entity';
import { Modal, ModalBody, ModalContent } from '../../../../../common-utils/components/ui/modal';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/common-utils/components/ui/form';
import { Input } from '@/common-utils/components/ui/input';
// Common
import { useToast } from '@/common-utils/hooks/use-toast';
// Lang
import { nsAccount } from '@/app/i18n/namespaces';

/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @param root0.merchantAccountId
 * @param root0.merchantAccountLabel
 * @param root0.setLaunchRefetch
 * @returns {JSX} ModalUpdateLabel
 */
function ModalUpdateLabel({ isOpen, setIsOpen, merchantAccountId, merchantAccountLabel, setLaunchRefetch }) {
    // Toast
    const { toast } = useToast();

    // Form
    const formSchema = z.object({
        label: z.string()
            .nonempty()
            .min(1, '') // At least 1 character
            .max(100, '') // Max 100 characters
    });
    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: { label: merchantAccountLabel }
    });

    // State
    const [showSubmitButton, setShowSubmitButton] = useState(true);

    // Api calls
    const onSuccess = () => {
        setShowSubmitButton(false);
        setIsOpen(false);
        setLaunchRefetch(true);
        toast({
            description: <Entity entity='modal.update_merchant_label.success' namespace={nsAccount} />,
            variant: 'success'
        });
    };
    const onError = () => {
        setShowSubmitButton(false);
        setIsOpen(false);
        toast({
            description: <Entity entity='modal.update_merchant_label.error' namespace={nsAccount} />,
            variant: 'danger'
        });
    };
    const submitUpdateLabel = actionsMerchantAccounts.updateLabel(onSuccess, onError);

    // Functions
    const onSubmit = (data) => {
        submitUpdateLabel.mutate({ contractAccessId: merchantAccountId, label: data.label });
    };
    const onClose = () => {
        setIsOpen(false);
        if (submitUpdateLabel.isSuccess) {
            setLaunchRefetch(true);
        }
    };

    return <Modal open={isOpen} onOpenChange={onClose}>
        <ModalContent className={cn('max-w-lg')}>
            <HeaderTitle title='modal.update_merchant_label.title' namespace={nsAccount} />
            <ModalBody>
                <Form {...form} onSubmit={form.handleSubmit(onSubmit)}>
                    <FormLabel htmlFor='label' className='block text-sm font-medium text-gray-700'>
                        <Entity entity='modal.update_merchant_label.label' namespace={nsAccount} />
                    </FormLabel>
                    <FormField
                        name='label'
                        control={form.control}
                        render={({ field }) => (
                            <FormItem className='addresses-list-input'>
                                <FormControl>
                                    <Input
                                        {...field}
                                        autoFocus
                                        type='text'
                                        entity='modal.update_merchant_label.placeholder'
                                        namespace={nsAccount}
                                        autoComplete='off'
                                        value={field.value}
                                        onChange={(e) => field.onChange(e.target.value)}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </Form>
            </ModalBody>
            <FooterForm
                onSubmit={form.handleSubmit(onSubmit)}
                isSubmitting={submitUpdateLabel.isLoading}
                showSubmitButton={showSubmitButton}
                isSubmitDisabled={!form.formState.isValid}
            />
        </ModalContent>
    </Modal>;
}
ModalUpdateLabel.propTypes = {
    setLaunchRefetch: PropTypes.func,
    trigger: PropTypes.element,
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    merchantAccountId: PropTypes.string,
    merchantAccountLabel: PropTypes.string
};
export default ModalUpdateLabel;
